import type { Product, SearchFilters } from "@little-finds/core";

export const anyCharmCategory = "jellycat_bag_charm";
export const isAllProducts = (filters: SearchFilters) =>
  !filters.product_ids.length && !filters.category_ids.length;
const isCharm = (product: Product) =>
  product.brand === "Jellycat" && product.category === "bag_charm";

export function productIsSelected(filters: SearchFilters, product: Product) {
  return (
    isAllProducts(filters) ||
    filters.product_ids.includes(product.id) ||
    (isCharm(product) && filters.category_ids.includes(anyCharmCategory))
  );
}

export function toggleWishlistProduct(
  filters: SearchFilters,
  id: string,
  catalog: Product[],
): SearchFilters {
  const productIds = isAllProducts(filters)
    ? catalog.filter((p) => p.category !== "bag").map((p) => p.id)
    : filters.product_ids;
  const selected = productIds.includes(id);
  const variants = { ...filters.variants };
  if (selected) delete variants[id];
  return {
    ...filters,
    offset: 0,
    variants,
    product_ids: selected
      ? productIds.filter((p) => p !== id)
      : [...productIds, id],
  };
}

export function chooseCharmMode(
  filters: SearchFilters,
  mode: "any" | "specific",
  catalog: Product[],
): SearchFilters {
  const charmIds = catalog.filter(isCharm).map((p) => p.id);
  const variants = { ...filters.variants };
  for (const id of charmIds) delete variants[id];
  const otherIds = (
    isAllProducts(filters)
      ? catalog.filter((p) => p.category !== "bag").map((p) => p.id)
      : filters.product_ids
  ).filter((id) => !charmIds.includes(id));
  return {
    ...filters,
    offset: 0,
    variants,
    category_ids:
      mode === "any"
        ? [...new Set([...filters.category_ids, anyCharmCategory])]
        : filters.category_ids.filter((id) => id !== anyCharmCategory),
    product_ids: mode === "any" ? otherIds : [...otherIds, ...charmIds],
  };
}

export function setWishlistVariant(
  filters: SearchFilters,
  id: string,
  value: string,
  catalog: Product[],
): SearchFilters {
  let next = filters;
  if (
    catalog.some((p) => p.id === id && isCharm(p)) &&
    filters.category_ids.includes(anyCharmCategory)
  )
    next = chooseCharmMode(filters, "specific", catalog);
  const variants = { ...next.variants };
  if (value.trim()) variants[id] = value.trim();
  else delete variants[id];
  return {
    ...next,
    offset: 0,
    variants,
    product_ids:
      isAllProducts(next) || next.product_ids.includes(id)
        ? next.product_ids
        : [...next.product_ids, id],
  };
}

export function wishlistSummary(filters: SearchFilters, catalog: Product[]) {
  if (isAllProducts(filters))
    return {
      count: "All",
      title: "All products",
      detail: "Any catalog product",
    };
  const names = filters.product_ids.map((id) => {
    const name = catalog.find((p) => p.id === id)?.name ?? "Saved product";
    return filters.variants?.[id] ? `${name} (${filters.variants[id]})` : name;
  });
  if (filters.category_ids.includes(anyCharmCategory))
    names.push("Any Jellycat bag charm, including new releases");
  return {
    count: String(names.length),
    title: `${names.length} ${names.length === 1 ? "selection" : "selections"}`,
    detail: names.join(" · "),
  };
}
export function searchAreaSummary(filters: SearchFilters) {
  if (filters.bounds) return "Within your chosen map area";
  return filters.scope === "us"
    ? "Across the United States"
    : `Within ${filters.radius_miles.toLocaleString("en-US")} miles of your chosen area`;
}
